import PropTypes from 'prop-types'
import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react'
import { io } from 'socket.io-client'
import { toast } from '../lib/toast'
import { runWithConcurrency } from '../utils/concurrentQueue'
import { resolveSocketUrl } from '../utils/exportDownload'
import {
  abortServerSide,
  closeUploadSuccess,
  runMosaicUpload,
  runSingleUpload,
} from './gisUploadOrchestrator'
import {
  gisUploadReducer,
  initialState,
  matchUploadKey,
  parseProgressPayload,
  uploadKind,
} from './gisUploadReducer'

export const GisUploadContext = createContext(null)

const genKey = () =>
  globalThis.crypto?.randomUUID?.() ||
  `up-${Date.now()}-${Math.round(Math.random() * 1e6)}`

export function GisUploadProvider({ children }) {
  const [state, dispatch] = useReducer(gisUploadReducer, initialState)
  const uploadsRef = useRef(state.uploads)
  uploadsRef.current = state.uploads
  const abortControllers = useRef(new Map()) // key -> AbortController
  const paramsByKey = useRef(new Map()) // key -> { type, file?, tiles?, meta }
  // Une carte encore en file n'a pas d'AbortController : sans cette marque,
  // l'annuler ne ferait que retirer la carte, l'envoi partirait quand même.
  const annulees = useRef(new Set())

  const cleanupRefs = useCallback(key => {
    abortControllers.current.delete(key)
    paramsByKey.current.delete(key)
  }, [])

  // Rend la promesse du run : c'est elle qui libere une place dans un lot borne.
  const launch = useCallback((key, params) => {
    const controller = new AbortController()
    abortControllers.current.set(key, controller)
    const common = {
      key,
      meta: params.meta,
      signal: controller.signal,
      dispatch,
    }
    if (params.type === 'mosaic') {
      return runMosaicUpload({ ...common, tiles: params.tiles })
    }
    return runSingleUpload({
      ...common,
      kind: uploadKind(params.type),
      file: params.file,
    })
  }, [])

  const enregistrer = useCallback((key, params, message) => {
    paramsByKey.current.set(key, params)
    dispatch({
      type: 'ADD',
      upload: {
        key,
        type: params.type,
        fileName: params.file?.name || `${params.tiles?.length || 0} tuiles`,
        layerName: params.meta.layerName,
        rasterType: params.meta.rasterType || null,
        serverId: null,
        uploadId: null,
        phase: 'initiating',
        s3Pct: 0,
        djangoPct: 0,
        message,
        error: null,
      },
    })
  }, [])

  const startUpload = useCallback(
    params => {
      const key = genKey()
      enregistrer(key, params, 'Préparation…')
      launch(key, params)
      return key
    },
    [enregistrer, launch],
  )

  // Toutes les cartes apparaissent d'emblee, mais seules `concurrency` montent
  // a la fois : chaque fichier ouvre deja 4 PUT S3. Detache a dessein.
  const startUploadBatch = useCallback(
    (liste, { concurrency = 2 } = {}) => {
      const keys = liste.map(() => genKey())
      // ADD empile en tete : enregistrer a l'envers rend au lot son ordre.
      for (let i = liste.length - 1; i >= 0; i -= 1) {
        enregistrer(keys[i], liste[i], 'En attente…')
      }
      runWithConcurrency(
        liste.map(
          (params, i) => () =>
            annulees.current.delete(keys[i])
              ? undefined
              : launch(keys[i], params),
        ),
        concurrency,
      ).then(() => keys.forEach(k => annulees.current.delete(k)))
      return keys
    },
    [enregistrer, launch],
  )

  const cancelUpload = useCallback(
    key => {
      const u = uploadsRef.current.find(x => x.key === key)
      if (!u) return
      if (u.phase === 'initiating' || u.phase === 's3') {
        if (!abortControllers.current.has(key)) annulees.current.add(key)
        abortControllers.current.get(key)?.abort()
        // si /initiate a déjà répondu, vraie annulation serveur (row + multipart S3)
        if (u.serverId != null) abortServerSide(u.type, u.serverId)
        dispatch({ type: 'REMOVE', key })
        cleanupRefs(key)
      }
      // phase 'processing' : non annulable (UI désactive le bouton)
    },
    [cleanupRefs],
  )

  const dismissUpload = useCallback(
    key => {
      dispatch({ type: 'REMOVE', key })
      cleanupRefs(key)
    },
    [cleanupRefs],
  )

  const retryUpload = useCallback(
    key => {
      const params = paramsByKey.current.get(key)
      if (!params) return
      dispatch({ type: 'REMOVE', key })
      cleanupRefs(key)
      startUpload(params)
    },
    [cleanupRefs, startUpload],
  )

  // socket Django -> phase processing/ready/failed
  useEffect(() => {
    const socket = io(resolveSocketUrl(), {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    })
    const onProgress = payload => {
      const parsed = parseProgressPayload(payload)
      const key = matchUploadKey(uploadsRef.current, parsed)
      if (!key) return
      if (parsed.status === 'ready') {
        const name = uploadsRef.current.find(u => u.key === key)?.layerName
        closeUploadSuccess({
          dispatch,
          key,
          kind: parsed.kind,
          serverId: parsed.serverId,
          layerName: name,
        })
        cleanupRefs(key)
      } else if (parsed.status === 'failed') {
        const name = uploadsRef.current.find(u => u.key === key)?.layerName
        toast.error(`Échec de l'import « ${name || 'couche'} »`)
        dispatch({
          type: 'ERROR',
          key,
          error: parsed.message || 'Le traitement a échoué.',
        })
      } else {
        if (parsed.djangoPct != null)
          dispatch({
            type: 'DJANGO',
            key,
            pct: parsed.djangoPct,
            message: parsed.message,
          })
        dispatch({ type: 'PHASE', key, phase: 'processing' })
      }
    }
    socket.on('gis-raster-progress', onProgress)
    socket.on('gis-vector-progress', onProgress)
    socket.on('model3d-progress', onProgress)
    return () => {
      socket.off('gis-raster-progress', onProgress)
      socket.off('gis-vector-progress', onProgress)
      socket.off('model3d-progress', onProgress)
      socket.disconnect()
    }
  }, [cleanupRefs])

  const value = {
    uploads: state.uploads,
    startUpload,
    startUploadBatch,
    cancelUpload,
    retryUpload,
    dismissUpload,
  }
  return (
    <GisUploadContext.Provider value={value}>
      {children}
    </GisUploadContext.Provider>
  )
}

GisUploadProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
